import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Hammer, Rocket, Atom, ArrowRight } from 'lucide-react';

const Purdue: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Purdue University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#333] selection:bg-[#CEB888] selection:text-black">
            {/* Top Bar - Black */}
            <div className="bg-black text-[#CEB888] py-2 px-6 flex justify-end gap-6 text-xs font-bold uppercase tracking-wider">
                <a href="#" className="hover:text-white transition-colors">Directory</a>
                <a href="#" className="hover:text-white transition-colors">Campus Map</a>
                <a href="#" className="hover:text-white transition-colors">Careers</a>
            </div>

            {/* Nav */}
            <nav className="bg-black py-4 px-6 md:px-12 border-b border-[#333]">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <div className="bg-[#CEB888] text-black font-black text-2xl h-10 w-10 flex items-center justify-center rounded-sm">P</div>
                         <div className="flex flex-col">
                            <span className="text-white text-xl font-bold uppercase tracking-wider leading-none">{schoolName.split(' ')[0]}</span>
                            <span className="text-[#CEB888] text-sm font-bold uppercase tracking-wider leading-none">University</span>
                         </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-white text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#CEB888] transition-colors">About</a>
                        <a href="#" className="hover:text-[#CEB888] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#CEB888] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#CEB888] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#CEB888] transition-colors">Athletics</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#CEB888] hover:text-white" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#CEB888]" size={26} />
                    </div>
                </div>
            </nav>

            {/* Hero - Giant Leaps */}
            <header className="relative h-[650px] bg-black">
                <img 
                    src="https://images.unsplash.com/photo-1627443183561-399fa378255b?q=80&w=2070&auto=format&fit=crop" 
                    alt="Engineering Fountain" 
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                 {/* Gold Diagonal Overlay */}
                 <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-black via-transparent to-transparent"></div>

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-4xl mx-auto text-center text-white">
                         <span className="bg-[#CEB888] text-black font-black uppercase tracking-widest px-3 py-1 mb-6 inline-block text-xs">The Next Giant Leap</span>
                         <h1 className="text-5xl md:text-8xl font-black mb-8 italic uppercase leading-none">
                             Persistent <br/> Innovation.
                         </h1>
                         <Button className="bg-[#CEB888] hover:bg-[#b09b6b] text-black font-bold uppercase tracking-widest px-12 h-16 rounded-sm text-lg shadow-lg">
                             Visit {schoolName.split(' ')[0]}
                         </Button>
                     </div>
                </div>
            </header>

            {/* Stats - Gold & Black */}
            <section className="bg-black text-[#CEB888] py-24 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8 border border-[#333] hover:border-[#CEB888] transition-colors group">
                         <Rocket size={48} className="mx-auto mb-6 group-hover:scale-110 transition-transform" />
                         <h3 className="text-3xl font-black mb-2">Cradle of Astronauts</h3>
                         <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">27 Alumni Astronauts</p>
                     </div>
                      <div className="p-8 border border-[#333] hover:border-[#CEB888] transition-colors group">
                         <Hammer size={48} className="mx-auto mb-6 group-hover:scale-110 transition-transform" />
                         <h3 className="text-3xl font-black mb-2">#4 Best Engineering</h3>
                         <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">Graduate Program (US News)</p>
                     </div>
                      <div className="p-8 border border-[#333] hover:border-[#CEB888] transition-colors group">
                         <Atom size={48} className="mx-auto mb-6 group-hover:scale-110 transition-transform" />
                         <h3 className="text-3xl font-black mb-2">R1 Research</h3>
                         <p className="text-gray-400 font-bold uppercase text-xs tracking-widest">Top-Tier Research Institution</p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-white py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <div className="flex justify-between items-end mb-12 border-b-2 border-black pb-4">
                         <h2 className="text-4xl font-black text-black uppercase italic">{schoolName} News</h2>
                         <a href="#" className="font-bold text-[#CEB888] uppercase text-xs hover:text-black mb-2">View Archive</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                         {[
                             "Hypersonic research facility breaks ground at Discovery Park",
                             "Agriculture students develop new sustainable farming methods",
                             `${schoolName} bands return to Ireland for St. Patrick's Day parade`
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="h-64 mb-6 overflow-hidden bg-gray-100 rounded-sm">
                                     <img src={`https://source.unsplash.com/random/400x300?sig=${i+30}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="News"/>
                                 </div>
                                 <h3 className="text-2xl font-bold text-black group-hover:text-[#CEB888] transition-colors leading-tight mb-4 ">{title}</h3>
                                  <div className="flex items-center gap-2 text-black font-bold uppercase text-xs tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                     Read More <ArrowRight size={14}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#1a1a1a] text-gray-400 py-16 px-6 md:px-12 border-t border-[#333]">
                  <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12 font-sans text-sm">
                      <div className="max-w-xs">
                          <div className="bg-[#CEB888] text-black font-black uppercase tracking-wider px-2 py-1 inline-block mb-6 text-xl">{schoolName.split(' ')[0]}</div>
                          <p className="mb-6">
                              610 Purdue Mall <br/>
                              West Lafayette, IN 47907 <br/>
                              765-494-4600
                          </p>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 font-bold uppercase text-xs tracking-widest text-[#CEB888]">
                          <ul className="space-y-4 hover:[&_a]:text-white">
                              <li><a href="#">Communication</a></li>
                              <li><a href="#">Campus Map</a></li>
                              <li><a href="#">Information</a></li>
                          </ul>
                          <ul className="space-y-4 hover:[&_a]:text-white">
                               <li><a href="#">Careers</a></li>
                              <li><a href="#">Contact</a></li>
                              <li><a href="#">Diversity</a></li>
                          </ul>
                      </div>
                  </div>
                  <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-600 border-t border-gray-800 pt-8">
                      © 2026 {schoolName}
                  </div>
             </footer>
        </div>
    );
};

export default Purdue;
